import type { ServiceCode } from '@common/enums';
import type { IServiceActivity } from '@common/interfaces';
import type { SettingName } from '@common/utils/settings';
import { CustomControlCode } from '@common/enums';
import { SettingAnswerTypes } from '@frontend/components/pages/carePath/constants/settingsLists';
import { getSelectedLanguage } from '@frontend/translate';

export type IServiceEmploymentSettingList = {
    [key: string]: IServiceEmploymentSetting;
};
export interface IServiceEmploymentSetting {
    conditionKey?: string;
    conditionServiceCodes?: ServiceCode[];
    header: string;
    label: string;
    options?: string[];
    type: SettingAnswerTypes;
}
export type ServiceActivity = Omit<IServiceActivity, 'label'>;

export type ServiceEmploymentSettingType = Extract<
    SettingName,
    | 'canEditGroupSession'
    | 'canEditMdo'
    | 'canExportForDato'
    | 'canReadBariPreDashboard'
    | 'canReadDashboard'
    | 'canReadOsasDashboard'
    | 'canReadGroupSession'
    | 'canReadMdo'
    | 'canReadQuestionnaireResponses'
    | 'canReadTotalWeightLoss'
    | 'canRegisterGroupSessionAttendance'
    | 'canStartProcess'
    | 'isAutoCloseRequest'
    | 'isRequester'
>;

function getServiceEmploymentSettingsLabelsByCustomControlCode(
    customControlCode: CustomControlCode
): ServiceEmploymentSettingType[] {
    switch (customControlCode) {
        case CustomControlCode.BariPreReferral:
            return ['canStartProcess'];
        case CustomControlCode.BariPreMdoInterimReport:
        case CustomControlCode.BariPreMdoReport:
        case CustomControlCode.BariPreMedConsultAssessment:
        case CustomControlCode.BariPreResumeMdo:
        case CustomControlCode.BariPreResumeMdoInterim:
        case CustomControlCode.BariPreGroupSession:
            return [
                'canReadBariPreDashboard',
                'canEditGroupSession',
                'canReadGroupSession',
                'canRegisterGroupSessionAttendance',
                'canEditMdo',
                'canReadMdo',
            ];
        case CustomControlCode.BariPostCaroQuestionnaireResponse:
            return ['canReadQuestionnaireResponses'];
        case CustomControlCode.BariPostCaroBodyWeightMeasurements:
            return ['canReadTotalWeightLoss'];
        case CustomControlCode.BariPostEnrollment:
            return ['canExportForDato'];
        case CustomControlCode.BariPostPoliReport:
            return ['canReadDashboard'];
        case CustomControlCode.TPCFlexRequest:
            return ['canStartProcess', 'isAutoCloseRequest'];
        case CustomControlCode.TPOFlexBiometry:
            return ['canStartProcess'];
        case CustomControlCode.OSASReferral:
        case CustomControlCode.OSASZorgDomeinReferral:
        case CustomControlCode.OSASCloudPatExamination:
        case CustomControlCode.OSASEvaluation:
        case CustomControlCode.OSASConsult:
        case CustomControlCode.OSASTreatmentChoice:
        case CustomControlCode.OSASCloudPatFollowUpExamination:
        case CustomControlCode.OSASFollowUpConsult:
            return ['canReadOsasDashboard'];
        default:
            return [];
    }
}

export function getServiceEmploymentSettingsLabels(
    customControlCodes: CustomControlCode[]
): ServiceEmploymentSettingType[] {
    const labels: ServiceEmploymentSettingType[] = [];

    customControlCodes.forEach((customControlCode) => {
        labels.push(...getServiceEmploymentSettingsLabelsByCustomControlCode(customControlCode));
    });

    labels.push('isRequester');

    // Return unique labels
    return [...new Set(labels)];
}

export function getServiceEmploymentSettingList(
    customControlCodes: CustomControlCode[]
): IServiceEmploymentSettingList {
    return getServiceEmploymentSettingListFromSettingNames(getServiceEmploymentSettingsLabels(customControlCodes));
}

export function getServiceEmploymentSettingListFromSettingNames(
    settingNamesList: string[]
): IServiceEmploymentSettingList {
    const settings: IServiceEmploymentSettingList = {};

    // Start request
    if (settingNamesList.includes('canStartProcess')) {
        settings.canStartProcess = {
            header: getSelectedLanguage().addCarePathSettingsHeaders.startRequest,
            label: getSelectedLanguage().addCarePathSettingsLabelsServiceEmployment.canStartProcess,
            type: SettingAnswerTypes.boolean,
        };
    }
    if (settingNamesList.includes('isRequester')) {
        settings.isRequester = {
            header: getSelectedLanguage().addCarePathSettingsHeaders.startRequest,
            label: getSelectedLanguage().addCarePathSettingsLabelsServiceEmployment.isRequester,
            type: SettingAnswerTypes.boolean,
        };
    }

    // Receive request
    if (settingNamesList.includes('isRecipient')) {
        settings.isRecipient = {
            header: getSelectedLanguage().addCarePathSettingsHeaders.receiveRequest,
            label: getSelectedLanguage().addCarePathSettingsLabelsServiceEmployment.isRecipient,
            type: SettingAnswerTypes.boolean,
        };
    }

    // Close service and subsequent service
    if (settingNamesList.includes('isAutoCloseRequest')) {
        settings.isAutoCloseRequest = {
            header: getSelectedLanguage().addCarePathSettingsHeaders.closeServiceAndSubsequentService,
            label: getSelectedLanguage().addCarePathSettingsLabelsServiceEmployment.isAutoCloseRequest,
            type: SettingAnswerTypes.boolean,
        };
    }

    // Group meeting management
    if (settingNamesList.includes('canReadMdo')) {
        settings.canReadMdo = {
            header: getSelectedLanguage().addCarePathSettingsHeaders.groupMeetingManagement,
            label: getSelectedLanguage().addCarePathSettingsHeaders.canReadMdo,
            type: SettingAnswerTypes.boolean,
        };
    }
    if (settingNamesList.includes('canEditMdo')) {
        settings.canEditMdo = {
            header: getSelectedLanguage().addCarePathSettingsHeaders.groupMeetingManagement,
            label: getSelectedLanguage().addCarePathSettingsHeaders.canEditMdo,
            type: SettingAnswerTypes.boolean,
        };
    }
    if (settingNamesList.includes('canReadGroupSession')) {
        settings.canReadGroupSession = {
            header: getSelectedLanguage().addCarePathSettingsHeaders.groupMeetingManagement,
            label: getSelectedLanguage().addCarePathSettingsHeaders.canReadGroupSession,
            type: SettingAnswerTypes.boolean,
        };
    }
    if (settingNamesList.includes('canEditGroupSession')) {
        settings.canEditGroupSession = {
            header: getSelectedLanguage().addCarePathSettingsHeaders.groupMeetingManagement,
            label: getSelectedLanguage().addCarePathSettingsHeaders.canEditGroupSession,
            type: SettingAnswerTypes.boolean,
        };
    }
    if (settingNamesList.includes('canRegisterGroupSessionAttendance')) {
        settings.canRegisterGroupSessionAttendance = {
            header: getSelectedLanguage().addCarePathSettingsHeaders.groupMeetingManagement,
            label: getSelectedLanguage().addCarePathSettingsHeaders.canRegisterGroupSessionAttendance,
            type: SettingAnswerTypes.boolean,
        };
    }

    // Dashboard management
    if (settingNamesList.includes('canReadBariPreDashboard')) {
        settings.canReadBariPreDashboard = {
            header: getSelectedLanguage().addCarePathSettingsHeaders.dashboardManagement,
            label: getSelectedLanguage().addCarePathSettingsHeaders.canReadDashboard,
            type: SettingAnswerTypes.boolean,
        };
    }
    if (settingNamesList.includes('canReadDashboard')) {
        settings.canReadDashboard = {
            header: getSelectedLanguage().addCarePathSettingsHeaders.dashboardManagement,
            label: getSelectedLanguage().addCarePathSettingsHeaders.canReadDashboard,
            type: SettingAnswerTypes.boolean,
        };
    }

    if (settingNamesList.includes('canReadOsasDashboard')) {
        settings.canReadOsasDashboard = {
            header: getSelectedLanguage().addCarePathSettingsHeaders.dashboardManagement,
            label: getSelectedLanguage().addCarePathSettingsHeaders.canReadDashboard,
            type: SettingAnswerTypes.boolean,
        };
    }

    // Monitoring management
    if (settingNamesList.includes('canReadQuestionnaireResponses')) {
        settings.canReadQuestionnaireResponses = {
            header: getSelectedLanguage().addCarePathSettingsHeaders.monitoringManagement,
            label: getSelectedLanguage().addCarePathSettingsHeaders.canReadQuestionnaireResponses,
            type: SettingAnswerTypes.boolean,
        };
    }
    if (settingNamesList.includes('canReadTotalWeightLoss')) {
        settings.canReadTotalWeightLoss = {
            header: getSelectedLanguage().addCarePathSettingsHeaders.monitoringManagement,
            label: getSelectedLanguage().addCarePathSettingsHeaders.canReadTotalWeightLoss,
            type: SettingAnswerTypes.boolean,
        };
    }

    // Export management
    if (settingNamesList.includes('canExportForDato')) {
        settings.canExportForDato = {
            header: getSelectedLanguage().addCarePathSettingsHeaders.exportManagement,
            label: getSelectedLanguage().addCarePathSettingsHeaders.canExportForDato,
            type: SettingAnswerTypes.boolean,
        };
    }

    return settings;
}
